import {
    useUser, useProjects, useExpenses, useGetLeads, useDepartments, useTotalStaff
} from "../Use-auth";
import moment from "moment";
import { useDateRange } from "../Pages/DateRangeContext";
import {
    UserRound, Volleyball, Star, AlignStartVertical, CircleCheck, TrendingUp, DollarSign
} from "lucide-react";
import {
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line,
} from "recharts";
import React, { useMemo, useState, useEffect } from "react";
import TaskEmployees from "./TaskEmployees";
import TaskPriority from "./TaskPriority";
import SkeletonPage from "./SkeletonPage ";
import { Link } from "wouter";
import { getStatusColor } from "../Pages/utils/taskUtils";
import ChartCard from "./ChartCard";
import Card from "./Cards";
import { useLocation } from "wouter";
import { tailwindBgToHex } from "../Pages/utils/taskUtils";

const formatStatusLabel = (status = "") =>
    status
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

const chartConfigs = {
    daily: {
        expence: {
            title: "Today's Expense",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Expense today"
        },
        payroll: {
            title: "Today's Payroll",
            icon: <DollarSign className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Payroll today"
        },
        projects: {
            title: "Today's Projects",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Projects today"
        },
        sales: {
            title: "Today's Leads",
            icon: <DollarSign className="w-16 h-16 mx-auto text-green-500 mb-2" />,
            description: "Leads today"
        },
    },

    weekly: {
        expence: {
            title: "Weekly Expense",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Expense this week"
        },
        payroll: {
            title: "Weekly Payroll",
            icon: <DollarSign className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Payroll this week"
        },
        projects: {
            title: "Weekly Projects",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Projects this week"
        },
        sales: {
            title: "Weekly Leads",
            icon: <DollarSign className="w-16 h-16 mx-auto text-green-500 mb-2" />,
            description: "Leads this week"
        },
    },

    monthly: {
        expence: {
            title: "Monthly Expense",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Expense this month"
        },
        payroll: {
            title: "Monthly Payroll",
            icon: <DollarSign className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Payroll this month"
        },
        projects: {
            title: "Monthly Projects",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Projects this month"
        },
        sales: {
            title: "Monthly Leads",
            icon: <DollarSign className="w-16 h-16 mx-auto text-green-500 mb-2" />,
            description: "Leads this month"
        },
    },

    yearly: {
        expence: {
            title: "Yearly Expense",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Expense this year"
        },
        payroll: {
            title: "Yearly Payroll",
            icon: <DollarSign className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Payroll this year"
        },
        projects: {
            title: "Yearly Projects",
            icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
            description: "Projects this year"
        },
        sales: {
            title: "Yearly Leads",
            icon: <DollarSign className="w-16 h-16 mx-auto text-green-500 mb-2" />,
            description: "Leads this year"
        },
    }
};

const getLabelByRange = (date, rangeType) => {
    if (rangeType === "daily") return "Today";
    if (rangeType === "weekly") return date.format("ddd");
    if (rangeType === "monthly") return date.format("D");
    if (rangeType === "yearly") return date.format("MMM");
};

export default function AdminDashPage() {
    const { start, end, rangeType } = useDateRange();
    const { data: totalStaff = [] } = useTotalStaff();
    const { data: projects = [], isLoading: projectsLoading } = useProjects();
    const { data: employees, isLoading: employeesLoading } = useTotalStaff();
    const { data: expensesData = [], isLoading: fetching } = useExpenses();
    const expenses = expensesData || [];
    const { data: response } = useGetLeads();
    const [activeTab, setActiveTab] = useState("expense");
    const { data: departments = [] } = useDepartments();
    const isTasksLoading = projectsLoading || employeesLoading;
    const [, navigate] = useLocation();

    const isWithinRange = (date) => {
        if (!start || !end) return true;
        const projectDate = moment(date);
        return projectDate.isBetween(moment(start), moment(end), undefined, '[]');
    };
    const filteredProjects = useMemo(() => projects.filter(p => isWithinRange(p.updatedAt || p.createdAt)), [projects, start, end]);

    const allUsersData = Array.isArray(response?.data)
        ? response.data
        : [];

    const allLeads = allUsersData;

    const onlyWonLeads = allLeads.filter(
        (lead) => lead.leadStatus?.trim().toLowerCase() === "won"
    );

    const totalLeads = Array.from(
        new Map(onlyWonLeads.map((l) => [l.id || l._id, l])).values()
    ).filter((lead) => {
        if (!start || !end) return true;

        const d = new Date(lead.createdAt);
        return d >= start && d <= end;
    });

    const totalProjectsBudget = totalLeads.reduce((sum, lead) => {
        const leadBudget = (lead.projects || []).reduce(
            (s, project) => s + Number(project.budget || 0),
            0
        );
        return sum + leadBudget;
    }, 0);

    const filteredExpenses = expenses
        .flatMap(report =>
            (report.expenses || [])
                .map(exp => ({ ...exp, reportName: report.report, userId: report.userId }))
        );

    const dateFilteredExpenses = filteredExpenses.filter(exp => {
        if (!start || !end) return true;
        const d = new Date(exp.expenseDate || exp.createdAt || exp.date);

        return d >= start && d <= end;
    });

    const totalExpense = dateFilteredExpenses.reduce(
        (sum, exp) => sum + Number(exp.amount || 0),
        0
    );

    const allEmployees = totalStaff || [];
    const activeEmployees = allEmployees.filter(
        (e) => e.employeeStatus === "Active"
    );
    const totalActiveStaff = activeEmployees.length;
    const currentChart = chartConfigs[rangeType] || chartConfigs.daily;

    let labels = [];
    if (rangeType === "daily") {
        labels = [moment(start).format("YYYY-MM-DD")];
    } else if (rangeType === "weekly") {
        labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    } else if (rangeType === "monthly") {
        const daysInMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
        labels = Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString());
    } else if (rangeType === "yearly") {
        labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    }

    const filteredEmployees = departments
        .filter(d => d.departmentName !== "Admin")
        .flatMap(d => d.employees || []);

    const payrollByLabel = labels.map((label, index) => {
        let totalSalary = 0;
        let totalBonus = 0;
        let totalDeductions = 0;
        let totalIncrement = 0;

        filteredEmployees.forEach(emp => {
            emp.payroll?.forEach(p => {

                const date = new Date(
                    p.workInformation?.createdAt ||
                    p.createdAt ||
                    p.updatedAt
                );

                if (start && end) {
                    if (date < start || date > end) return;
                }

                if (rangeType === "daily") {
                    if (date.toDateString() === new Date(start).toDateString()) {
                        totalSalary += p.netSalary || 0;
                        totalBonus += p.earnings?.performanceBonus || 0;
                        totalDeductions += p.deductions?.totalDeductions || 0;
                        totalIncrement += p.earnings?.incrementAmount || 0;
                    }
                }

                else if (rangeType === "weekly") {
                    const dayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1;

                    if (dayIndex === index) {
                        totalSalary += p.netSalary || 0;
                        totalBonus += p.earnings?.performanceBonus || 0;
                        totalDeductions += p.deductions?.totalDeductions || 0;
                        totalIncrement += p.earnings?.incrementAmount || 0;
                    }
                }

                else if (rangeType === "monthly") {
                    if (date.getDate() === index + 1) {
                        totalSalary += p.netSalary || 0;
                        totalBonus += p.earnings?.performanceBonus || 0;
                        totalDeductions += p.deductions?.totalDeductions || 0;
                        totalIncrement += p.earnings?.incrementAmount || 0;
                    }
                }

                else if (rangeType === "yearly") {
                    if (date.getMonth() === index) {
                        totalSalary += p.netSalary || 0;
                        totalBonus += p.earnings?.performanceBonus || 0;
                        totalDeductions += p.deductions?.totalDeductions || 0;
                        totalIncrement += p.earnings?.incrementAmount || 0;
                    }
                }

            });
        });

        return {
            label,
            totalSalary,
            totalBonus,
            totalDeductions,
            totalIncrement
        };
    });

    const expenseChartData = useMemo(() => {
        if (!start || !end) return [];

        return labels.map((label, index) => {
            let total = 0;
            dateFilteredExpenses.forEach(exp => {

                const d = new Date(exp.createdAt || exp.date);
                if (rangeType === "daily") {
                    const today = new Date(start).toDateString();

                    if (d.toDateString() === today) {
                        total += Number(exp.amount || 0);
                    }
                }

                else if (rangeType === "weekly") {
                    const dayIndex = d.getDay() === 0 ? 6 : d.getDay() - 1;

                    if (dayIndex === index) {
                        total += Number(exp.amount || 0);
                    }
                }

                else if (rangeType === "monthly") {
                    const day = d.getDate();

                    if (day === index + 1) {
                        total += Number(exp.amount || 0);
                    }
                }

                else if (rangeType === "yearly") {
                    const month = d.getMonth();

                    if (month === index) {
                        total += Number(exp.amount || 0);
                    }
                }

            });

            return {
                label,
                expence: total,
            };
        });

    }, [dateFilteredExpenses, labels, rangeType, start, end]);

    const { data: leadData } = useGetLeads();

    const response1 = Array.isArray(leadData) ? leadData : leadData?.data || [];

    const allWonLeads = Array.isArray(response1) ? response1 : [];

    const filteredLeads = useMemo(() => {
        return allWonLeads.filter(l => {
            if (!l?.createdAt) return false;

            const status = l.leadStatus?.toLowerCase();
            return status === "won" && isWithinRange(l.createdAt);
        });
    }, [allWonLeads, start, end]);

    const chartData = useMemo(() => {
        let data = [];

        if (rangeType === "daily") {
            data = [{ label: "Today", total: 0, active: 0, lost: 0, leadsWon: 0, leadsLost: 0, employees: 0 }];
        } else if (rangeType === "weekly") {
            const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
            data = days.map(day => ({
                label: day,
                total: 0,
                active: 0,
                lost: 0,
                leadsWon: 0,
                leadsLost: 0,
                employees: 0
            }));
        } else if (rangeType === "monthly") {
            const daysInMonth = moment(start).daysInMonth();
            data = Array.from({ length: daysInMonth }, (_, i) => ({
                label: (i + 1).toString(),
                total: 0,
                active: 0,
                lost: 0,
                leadsWon: 0,
                leadsLost: 0,
                employees: 0
            }));
        } else if (rangeType === "yearly") {
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            data = months.map(m => ({
                label: m,
                total: 0,
                active: 0,
                lost: 0,
                leadsWon: 0,
                leadsLost: 0,
                employees: 0
            }));
        }

        data.forEach(item => {
            filteredProjects.forEach(p => {
                const date = moment(p.updatedAt || p.createdAt);
                const key = getLabelByRange(date, rangeType);
                if (key === item.label) {
                    item.total++;
                    if (p.status === "Lost") item.lost++;
                    else item.active++;
                }
            });
        });

        filteredLeads.forEach(l => {
            const key = getLabelByRange(moment(l.createdAt), rangeType);
            const item = data.find(d => d.label === key);
            if (item) {
                if (l.leadStatus === "Won") item.leadsWon += 1;
                if (l.leadStatus === "Lost") item.leadsLost += 1;
            }
        });

        totalStaff.forEach(e => {
            if (!e?.createdAt) return;
            const key = getLabelByRange(moment(e.createdAt), rangeType);
            const item = data.find(d => d.label === key);
            if (item) item.employees++;
        });

        return data;
    }, [filteredProjects, filteredLeads, totalStaff, rangeType, start, end]);

    function getAllTasks(project, parentId = null, parentName = null) {
        let tasks = [];

        const statusTask = project.statusTask || {};

        Object.entries(statusTask).forEach(([status, arr]) => {
            arr.forEach(task => {
                tasks.push({
                    ...task,
                    projectId: parentId || project._id,

                    projectName: parentName
                        ? `${parentName} / ${project.projectName}`
                        : project.projectName,

                    status,
                });
            });
        });

        if (project.subProjects?.length) {
            project.subProjects.forEach(sub => {
                tasks = tasks.concat(
                    getAllTasks(
                        sub,
                        project._id,
                        parentName ? `${parentName} / ${project.projectName}` : project.projectName
                    )
                );
            });
        }

        return tasks;
    }

    let myTasks = filteredProjects
        .flatMap(p => getAllTasks(p))
        .filter(task => {
            const taskDate = new Date(task.updatedAt || task.createdAt);

            if (!start || !end) return true;

            return taskDate >= start && taskDate <= end;
        });

    myTasks = myTasks.filter(
        (task) => task.status !== "completed" && task.status !== "complete"
    );

    myTasks = myTasks
        .sort((a, b) => {
            const d1 = new Date(a.updatedAt || a.createdAt);
            const d2 = new Date(b.updatedAt || b.createdAt);
            return d2 - d1;
        })
        .slice(0, 10);

    const groupedTasks = React.useMemo(() => {
        return myTasks.reduce((acc, task) => {
            const name = task.projectName || "Unknown Project";

            if (!acc[name]) {
                acc[name] = [];
            }

            acc[name].push(task);

            return acc;
        }, {});
    }, [myTasks]);

    const onTaskClick = (projectId, subProjectId, taskId) => {
        navigate(
            `/projects?tab=list&projectId=${projectId}&subProjectId=${subProjectId || ""}&taskId=${taskId || ""}`
        );
    };

    const getIconColorFromStatus = (status,) => {

        const bgClass = getStatusColor(status);

        const bg = bgClass.split(" ")[0];

        return tailwindBgToHex[bg] || "#9ca3af";
    };

    return (
        <div className="min-h-screen p-2 rs:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-4 sm:mb-8">
                <Link href="/myTeam"><Card title="Total Active Staff" value={totalActiveStaff || "-"} icon={<UserRound className="opacity-80" />} bg="from-blue-500 to-blue-600" /></Link>
                <Link href="/hr?tab=expenses"> <Card title="Total Expense" value={fetching
                    ? "..."
                    : "₹" + totalExpense.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })} icon={<Volleyball className="opacity-80" />} bg="from-green-500 to-green-600" /></Link>
                <Link href="/lead-contacts"><Card title="Total Sales" value={`$${totalProjectsBudget}` || "-"} icon={<Star className="opacity-80" />} bg="from-purple-500 to-purple-600" /></Link>
                <Link href="/projects?tab=list&filter=all"> <Card title="Total Projects" value={filteredProjects.length || "-"} icon={<AlignStartVertical className="opacity-80" />} bg="from-orange-500 to-orange-600" /></Link>
            </div>

            <div className="bg-white rounded-lg sm:rounded-2xl p-2 sm:p-6 shadow-lg">
                <h3 className="font-bold text-sm sm:text-xl mb-4">Recent Tasks</h3>

                {isTasksLoading ? (
                    <SkeletonPage />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs sm:text-sm">
                            <thead className="border-b">
                                <tr>
                                    <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider lg:px-3 lg:py-2 lg:text-sm">Title</th>
                                    <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider lg:px-3 lg:py-2 lg:text-sm hidden xs:table-cell">Status</th>
                                    <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider lg:px-3 lg:py-2 lg:text-sm">Priority</th>
                                    <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider lg:px-3 lg:py-2 lg:text-sm hidden zx:table-cell">Assigned</th>
                                    <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider lg:px-3 lg:py-2 lg:text-sm hidden custom:table-cell">Updated</th>
                                </tr>
                            </thead>

                            <tbody>
                                {Object.keys(groupedTasks).length > 0 ? (
                                    Object.entries(groupedTasks).map(([projectName, tasks]) => (
                                        <React.Fragment key={projectName}>
                                            <tr className={` border-b  border-gray-200 bg-[#dfeaf6]`}>
                                                <td
                                                    colSpan="7"
                                                    className="rounded-md px-1 py-1 lg:px-3 lg:py-2 font-medium text-gray-900 text-lg border-b border-gray-200"
                                                >
                                                    <div className="flex items-center gap-2 sm:gap-3">
                                                        <span className=" text-black px-1 sm:px-3 py-0 sm:py-1 rounded sm:rounded-lg text-[12px] sm:text-[16px] font-semibold">
                                                            {projectName}
                                                        </span>

                                                        <span className="text-gray-600 text-xs lg:text-sm">
                                                            {tasks.length} Tasks
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>

                                            {/* Tasks */}
                                            {tasks.map(task => (
                                                <tr key={task._id} className="border-b text-xs lg:text-sm text-gray-600 cursor-pointer" onClick={() =>
                                                    onTaskClick(task.projectId, task.subProjectId, task._id, task.status)
                                                }>
                                                    <td className="px-2 py-1 text-left text-xs lg:text-sm text-gray-600 max-w-[250px]">
                                                          <div className="flex items-center gap-2">
                                                            <span className={`font-bold`}>
                                                                <CircleCheck
                                                                    className="w-4 h-4"
                                                                    color={getIconColorFromStatus(task.status)}
                                                                />
                                                            </span>
                                                            <span className="break-words">
                                                                {task?.title
                                                                    ? task.title.length > 20
                                                                        ? `${task.title.slice(0, 20)}...`
                                                                        : task.title
                                                                    : "Untitled"}
                                                            </span>
                                                        </div>
                                                    </td>

                                                    <td className="px-2 py-1 hidden xs:table-cell">
                                                        <span
                                                            className={`${getStatusColor(task.status)} text-white px-2 py-1 rounded sm:rounded-lg text-xs sm:text-sm`}
                                                        >
                                                            {formatStatusLabel(task.status)}
                                                        </span>
                                                    </td>

                                                    <td className="px-2 py-1">
                                                        <TaskPriority value={task.priority} showclear={false} />
                                                    </td>

                                                    <td className="px-2 py-1 hidden zx:table-cell">
                                                        <TaskEmployees
                                                            selected={task.assignedEmployees?.map((e) => e.userId) || []}
                                                            employees={employees || []}
                                                            showDropdown={false}
                                                            hideSelected
                                                        />
                                                    </td>

                                                    <td className="px-2 py-1 hidden custom:table-cell text-xs sm:text-sm">
                                                        {new Date(task.updatedAt || task.createdAt).toLocaleString()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan="5"
                                            className="text-center py-4 sm:py-6 text-gray-400 text-xs sm:text-sm"
                                        >
                                            No tasks found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-6 mt-4 sm:mt-8">
                <div className=" bg-white rounded-2xl shadow-lg p-1 sm:p-4">
                    <div className="flex border-b mb-4">
                        <button
                            onClick={() => setActiveTab("expense")}
                            className={`px-4 py-2 text-sm md:text-[16px] font-semibold ${activeTab === "expense"
                                ? "border-b-2 border-blue-500 text-blue-600"
                                : "text-gray-500"
                                }`}
                        >
                            Expense
                        </button>

                        <button
                            onClick={() => setActiveTab("payroll")}
                            className={`px-4 py-2 text-sm md:text-[16px] font-semibold ${activeTab === "payroll"
                                ? "border-b-2 border-blue-500 text-blue-600"
                                : "text-gray-500"
                                }`}
                        >
                            Payroll
                        </button>
                    </div>
                    {activeTab === "expense" && (
                        <ChartCard title={currentChart.expence.title} icon={currentChart.expence.icon} description={currentChart.expence.description}> <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={expenseChartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                                <CartesianGrid stroke="#000" strokeOpacity={0.1} strokeDasharray="3 3" />
                                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#555" }} />
                                <YAxis tick={{ fontSize: 12, fill: "#555" }} />
                                <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                                <Line type="monotone" dataKey="expence" stroke="#EF4444" strokeWidth={2} />
                            </LineChart>
                        </ResponsiveContainer>
                        </ChartCard>

                    )}

                    {activeTab === "payroll" && (

                        <ChartCard title={currentChart.payroll.title} icon={currentChart.payroll.icon} description={currentChart.payroll.description}
                        >
                            {filteredEmployees.length > 0 ? (
                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart
                                        data={payrollByLabel}
                                        margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                                    >
                                        <CartesianGrid stroke="#000" strokeOpacity={0.1} strokeDasharray="3 3" />
                                        <XAxis yAxisId="left" dataKey="label" tick={{ fontSize: 12, fill: "#555" }} />
                                        <YAxis yAxisId="right" tick={{ fontSize: 12, fill: "#555" }} />
                                        <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                                        <Line yAxisId="left" type="monotone" dataKey="totalSalary" stroke="#3b82f6" strokeWidth={2} name="Salary" />
                                        <Line yAxisId="right" type="monotone" dataKey="totalBonus" stroke="#10b981" strokeWidth={2} name="Bonus" />
                                        <Line yAxisId="right" type="monotone" dataKey="totalDeductions" stroke="#ef4444" strokeWidth={2} name="Deductions" />
                                        <Line yAxisId="right" type="monotone" dataKey="totalIncrement" stroke="#8b5cf6" strokeWidth={2} name="Increment" />
                                    </LineChart>
                                </ResponsiveContainer>

                            ) : (
                                <p className="text-center text-gray-400 mt-4">
                                    No payroll data available
                                </p>
                            )}
                        </ChartCard>
                    )}

                </div>
                <ChartCard title={currentChart.projects.title} icon={currentChart.projects.icon} description={currentChart.projects.description}>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" padding={{ left: 0, right: 0 }} />
                            <XAxis yAxisId="left" dataKey="label" tick={{ fontSize: 10, fill: "#555" }} padding={{ left: 0, right: 0 }} />
                            <YAxis yAxisId="right" tick={{ fontSize: 10, fill: "#555" }} width={30} />
                            <Tooltip />
                            <Line yAxisId="left" type="monotone" dataKey="total" stroke="#3B82F6" strokeWidth={2} name="Total Projects"></Line>
                            <Line yAxisId="right" type="monotone" dataKey="active" stroke="#22C55E" strokeWidth={2} name="Active Projects"></Line>
                            <Line yAxisId="right" type="monotone" dataKey="lost" stroke="#EF4444" strokeWidth={2} name="Lost Projects"></Line>
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title={currentChart.sales.title} icon={currentChart.sales.icon} description={currentChart.sales.description}>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart
                            data={chartData}
                            margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis yAxisId="left" dataKey="label" tick={{ fontSize: 10 }} />
                            <YAxis yAxisId="right" tick={{ fontSize: 10 }} width={35} />
                            <Tooltip />
                            <Line yAxisId="left" type="monotone" dataKey="leadsWon" stroke="#10B981" strokeWidth={2} name="Won Leads" />
                            <Line yAxisId="right" type="monotone" dataKey="leadsLost" stroke="#EF4444" strokeWidth={2} name="Lost Leads" />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        </div>
    )
}